import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../lib/api';
import { chatApi, sendAiMessage, type AiStreamHandlers } from '../../lib/chat';
import { useSessionStream } from '../../hooks/useSessionStream';
import { ChatHeader } from './ChatHeader';
import { ChatMessages } from './ChatMessages';
import { ChatInput } from './ChatInput';
import { toast } from 'sonner';
import type { ApiChatMessage, ApiChatSession, ChatMessage } from '../../types/chat';

export const ChatPage: React.FC = () => {
  const { user, setCredits } = useAuth();
  const [sessions, setSessions] = useState<ApiChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<ApiChatSession | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [aiPartial, setAiPartial] = useState<string | null>(null);
  const { messages, connected, mergeMessages } = useSessionStream(activeSession?.id ?? null);

  const loadSessions = useCallback(async () => {
    try {
      const { sessions: list } = await chatApi.listSessions();
      const aiSessions = list.filter(s => s.sessionType === 'ai')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setSessions(aiSessions);
      return aiSessions;
    } catch (err) {
      console.error('Chat: erro ao listar conversas', err);
      toast.error('Erro ao carregar suas conversas');
      return [];
    }
  }, []);

  // Primeiro load: lista + sessão ativa (última IA) + backfill
  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    (async () => {
      const aiSessions = await loadSessions();
      if (cancelled) return;

      if (aiSessions.length > 0) {
        setActiveSession(aiSessions[0]);
      } else {
        try {
          const { session } = await chatApi.createSession({ sessionType: 'ai' });
          if (!cancelled) {
            setActiveSession(session);
            setSessions([session]);
          }
        } catch (err) {
          if (!cancelled) toast.error('Erro ao iniciar a conversa. Tente recarregar.');
          console.error('Chat: erro ao criar sessão', err);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [user, loadSessions]);

  // Backfill ao trocar de sessão
  useEffect(() => {
    const sessionId = activeSession?.id;
    if (!sessionId) return;

    let cancelled = false;

    const loadHistory = async () => {
      try {
        const { messages: history } = await chatApi.getMessages(sessionId);
        if (!cancelled) mergeMessages(history);
      } catch (err) {
        if (!cancelled) toast.error('Erro ao carregar o histórico de mensagens');
        console.error('Chat: erro no backfill', err);
      }
    };

    loadHistory();
    return () => { cancelled = true; };
  }, [activeSession?.id, mergeMessages]);

  const handleNewChat = useCallback(async () => {
    try {
      const { session } = await chatApi.createSession({ sessionType: 'ai' });
      setActiveSession(session);
      setSessions(prev => [session, ...prev]);
    } catch (err) {
      toast.error('Erro ao criar nova conversa');
      console.error('Chat: erro ao criar nova conversa', err);
    }
  }, []);

  const handleSendMessage = async (content: string) => {
    if (!user) {
      toast.error('Por favor, faça login para continuar');
      return;
    }
    if (!activeSession) {
      toast.error('A conversa ainda está sendo preparada. Tente de novo em instantes.');
      return;
    }
    if (isSending) return;
    const sessionId = activeSession.id;

    setIsSending(true);
    setAiPartial('');
    const handlers: AiStreamHandlers = {
      onUser: (userMsg: ApiChatMessage) => {
        mergeMessages([userMsg]);
      },
      onDelta: (text: string) => {
        setAiPartial(prev => (prev ?? '') + text);
      },
    };

    try {
      const { aiMsg, credits } = await sendAiMessage(sessionId, content, handlers);
      // Stream de sessão IA usa SSE; frame done confirma c/ saldo
      setCredits(credits);
      mergeMessages([aiMsg]);
      // Mantém o item da lateral fresco (server só devolve lastMessage no list)
      setSessions(prev => prev.map(s => (s.id === sessionId)
        ? { ...s, lastMessage: aiMsg.content.slice(0, 80) }
        : s
      ));
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        toast.error('Créditos insuficientes para continuar');
      } else if (err instanceof ApiError) {
        // frame error do server: crédito devolvido, IA indisponível
        toast.error(err instanceof Error ? err.message : 'Erro ao enviar mensagem');
      } else {
        toast.error(err instanceof Error ? err.message : 'Erro ao enviar mensagem');
      }
    } finally {
      setIsSending(false);
      setAiPartial(null);
    }
  };

  const displayMessages: ChatMessage[] = messages.map(m => ({
    id: m.id,
    content: m.content,
    sender: m.senderRole,
    timestamp: new Date(m.createdAt)
  }));

  const natalContext = (activeSession?.context as { natal?: string } | null | undefined)?.natal;
  const lowCredits = !!user && typeof user.credits === 'number' && user.credits <= 2;

  // Prévia da home (?via=preview): placeholder contextual com o Sol detectado
  const [chatPlaceholder, setChatPlaceholder] = useState('Digite sua mensagem…');
  useEffect(() => {
    const viaPreview = new URLSearchParams(window.location.search).get('via') === 'preview';
    if (!viaPreview) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem('stela.preview') ?? 'null') as {
        sun?: string;
        nome?: string;
      } | null;
      if (saved?.sun) {
        setChatPlaceholder(
          saved.nome
            ? `${saved.nome}, pergunte sobre seu Sol em ${saved.sun}…`
            : `Pergunte sobre seu Sol em ${saved.sun}…`
        );
      }
    } catch {
      // storage bloqueado — placeholder padrão
    }
  }, []);
  const sessionTitle = (session: ApiChatSession) => {
    if (session.lastMessage) return session.lastMessage;
    const date = new Date(session.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    return `Conversa de ${date}`;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <ChatHeader onNewChat={handleNewChat} connected={connected} />

      <div className="flex flex-1 min-h-0 flex-col md:flex-row">
        {/* Lateral: Minhas conversas */}
        <aside className="md:w-60 md:border-r border-purple-500/20 md:bg-indigo-950/60 md:overflow-y-auto p-3">
          <p className="text-xs uppercase tracking-wide text-purple-200/50 mb-2 px-1">Minhas conversas</p>
          <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible">
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => setActiveSession(session)}
                className={`text-left text-sm rounded-lg px-3 py-2 transition-colors whitespace-nowrap md:whitespace-normal ${
                  session.id === activeSession?.id
                    ? 'bg-purple-500/30 text-purple-100'
                    : 'bg-white/5 text-purple-200/70 hover:bg-white/10'
                }`}
              >
                <span className="block truncate">{sessionTitle(session)}</span>
                <span className="block text-xs opacity-50 md:whitespace-normal">
                  {new Date(session.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Coluna principal */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0">
          {natalContext && (
            <div
              title={natalContext}
              className="mx-4 mt-3 self-start text-xs px-3 py-1 rounded-full bg-purple-500/20 text-purple-100 cursor-help"
            >
              🗺️ Mapa carregado
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4">
            {activeSession ? (
              <ChatMessages messages={displayMessages} isLoading={isSending && !aiPartial} error={null} />
            ) : (
              user && <div className="flex justify-center mt-8"><ChatMessages messages={[]} isLoading error={null} /></div>
            )}
            {/* Bolha da IA em construção (streaming) */}
            {aiPartial !== null && (
              <div className="mt-4">
                <ChatMessages
                  messages={[{ id: 'streaming-partial', content: aiPartial, sender: 'ai', timestamp: new Date() }]}
                  isLoading={aiPartial === ''}
                  error={null}
                />
              </div>
            )}
          </div>

          {lowCredits && (
            <div className="mx-4 mb-2 text-xs text-purple-200/70 bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2">
              Restam {user?.credits} conversas — em breve você poderá comprar mais
            </div>
          )}

          <div className="p-4 bg-indigo-950/90 border-t border-purple-500/20">
            <ChatInput
              onSendMessage={handleSendMessage}
              isLoading={isSending}
              placeholder={chatPlaceholder}
            />
          </div>
        </div>
      </div>
    </div>
  );
};